/* eslint-disable react/prop-types */
import { Box, Card, IconButton, LinearProgress, Typography } from '@mui/material';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { downloadReceiptDocument } from '../../api/get-properties';

import styles from './file-section.module.css';

import { StatusFile } from '@/lib/enums';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
interface FileSectionProps {
  readonly file: File;
  readonly fileName: string;
  readonly percent: number;
  readonly status: StatusFile;
  readonly handleRemoveFile: (fileName: string) => void;
  readonly handleReUploadFile: (fileName: string) => void;
}

const FileSection: React.FC<FileSectionProps> = ({
  file,
  fileName,
  percent,
  status,
  handleRemoveFile,
  handleReUploadFile,
}) => {
  const uploaded = Math.round((percent / 100) * file.size);
  const { setConfirmDialog } = useConfirmDialog();
  const getFileSize = (fileSize: number) => {
    if (fileSize < 1024) {
      return `${fileSize}B`;
    } else if (fileSize < 1024 * 1024) {
      return `${(fileSize / 1024).toFixed(2)}KB`;
    } else {
      return `${(fileSize / 1024 / 1024).toFixed(2)}MB`;
    }
  };

  // Helper function to render status display
  const renderStatusDisplay = () => {
    if (status === StatusFile.LOADING) {
      return 'Loading';
    }
    if (status === StatusFile.DONE) {
      return (
        <>
          <CheckIcon sx={{ width: '20px', height: '20px', verticalAlign: 'bottom' }} />
          Done
        </>
      );
    }
    return 'failed';
  };

  const handleDownloadFile = async () => {
    try {
      await downloadReceiptDocument(file, fileName);
    } catch (error) {
      const message = (error as Error).message;
      setConfirmDialog({
        title: message,
      });
    }
  };

  return (
    <Card
      sx={{
        marginBottom: '16px',
        marginTop: '16px',
        padding: '24px 16px',
        borderRadius: '8px',
        border: '1px solid #C4C6D0',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {status === StatusFile.FAILED ? (
            <WarningAmberOutlinedIcon
              sx={{
                fontSize: '24px',
                color: '#B00020',
                width: '24px',
                height: '24px',
                marginRight: '8px',
              }}
            />
          ) : (
            <InsertDriveFileOutlinedIcon
              sx={{
                fontSize: '24px',
                color: '#0047BA',
                width: '24px',
                height: '24px',
                marginRight: '8px',
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Typography variant='body1' fontWeight={400} color='#1A1C20' fontSize='16px'>
            {fileName}
          </Typography>
          <Box sx={{ display: 'inline-block' }}>
            <Typography
              color='#74777F'
              fontSize='14px'
              display='inline-block'
              sx={{ marginRight: '16px' }}
            >
              {getFileSize(uploaded)} of {getFileSize(file.size)}
            </Typography>
            <Typography
              color={status === StatusFile.FAILED ? '#B00020' : '#0047BA'}
              fontSize='14px'
              display='inline-block'
            >
              {renderStatusDisplay()}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {status === StatusFile.DONE && (
            <>
              <FileDownloadOutlinedIcon
                className={styles.iconButton}
                onClick={() => {
                  void handleDownloadFile();
                }}
              />
              <IconButton
                onClick={() => handleRemoveFile(fileName)}
                aria-label='Delete file'
                size='small'
                className={styles.iconButton}
              >
                <img src={`/images/trash.svg`} alt='Delete file' className={styles.imageButton} />
              </IconButton>
            </>
          )}
          {status === StatusFile.FAILED && (
            <LoopOutlinedIcon
              onClick={() => handleReUploadFile(fileName)}
              className={styles.iconButton}
            />
          )}
          <CloseIcon className={styles.iconButton} onClick={() => handleRemoveFile(fileName)} />
        </Box>
      </Box>
      {status !== StatusFile.DONE && (
        <Box sx={{ width: '100%', marginTop: '8px' }}>
          <LinearProgress
            variant='buffer'
            value={percent}
            valueBuffer={100}
            color={status === StatusFile.FAILED ? 'error' : 'primary'}
          />
        </Box>
      )}
    </Card>
  );
};

export default FileSection;
